import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * SearchableSelect - A searchable dropdown component
 * @param {Object} props
 * @param {Array} props.options - Array of {value: string, label: string}
 * @param {string} props.value - Selected value
 * @param {function} props.onChange - Callback when value changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.emptyText - Text to show when no results found
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {boolean} props.disabled - Whether the select is disabled
 * @param {string} props.className - Additional classes for the button
 * @param {boolean} props.allowClear - Allow clearing by selecting the current value
 */
export const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select option...",
  emptyText = "No option found.",
  searchPlaceholder = "Search...",
  disabled = false,
  className,
  allowClear = false,
}) => {
  const [open, setOpen] = React.useState(false);

  const selectedOption = options.find((option) => option.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-9 px-3 py-1 text-base md:text-sm font-normal dark:bg-background/30 bg-transparent",
            className,
          )}
          disabled={disabled}
        >
          {selectedOption ? selectedOption.label : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const optionValue = option.value ?? "";
                const optionLabel = String(
                  option.label ?? option.value ?? option.key ?? "",
                );
                return (
                  <CommandItem
                    key={optionValue}
                    value={optionLabel}
                    disabled={option.disabled}
                    className={cn(
                      option.disabled && "cursor-not-allowed opacity-60",
                    )}
                    onSelect={() => {
                      if (option.disabled) return;
                      const nextValue =
                        allowClear && optionValue === value ? "" : optionValue;
                      onChange(nextValue);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === optionValue ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {optionLabel}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
